import React, { useState, useEffect, useMemo } from "react";
import { Card, Container, Row, Col, Modal } from "react-bootstrap";
import { FaPlus, FaCalendarAlt, FaUser, FaEdit } from "react-icons/fa";
import { MdOutlineTextsms } from "react-icons/md";
import { ThumbsUp, ChevronDown, ChevronUp, Search, Inbox, ImageOff } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../Context/AuthContext";
import axios from "axios";
import { CheckCircleFill } from "react-bootstrap-icons";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "./UserDashboard.css";
import { useRef } from "react";
import ReopenComplaintModal from "../ReopenComplaintModal/ReopenComplaintModal";

// NoImageIcon inline fallback
const NoImageIcon = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='80' height='80' viewBox='0 0 24 24' fill='none' stroke='%23c4b5fd' stroke-width='1.5'%3E%3Crect x='3' y='3' width='18' height='18' rx='3'/%3E%3Ccircle cx='8.5' cy='8.5' r='1.5'/%3E%3Cpath d='m21 15-5-5L5 21'/%3E%3C/svg%3E";

const CATEGORIES = [
  "Infrastructure",
  "Canteen",
  "Examination",
  "Academics",
  "Fee Payments and Accounts",
  "EduPrime",
  "Hostel Food",
  "Extracurricular and Events",
  "Security",
  "Sports",
  "Housekeeping",
  "Audio-Visual Equipment",
  "Parking",
  "Transport",
  "Library",
  "IT and Networking",
  "Others",
];

const STATUSES = ["Pending", "Ongoing", "Resolved"];

const UserDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const userEmail = user?.email;
  const [complaints, setComplaints] = useState([]);
  const [userVotes, setUserVotes] = useState({});
  const [likingComplaintId, setLikingComplaintId] = useState(null);
  const [expandedCard, setExpandedCard] = useState(null);
  const [expandedDescriptions, setExpandedDescriptions] = useState({});
  const [editComplaint, setEditComplaint] = useState(null);
  const [editForm, setEditForm] = useState({ title: "", description: "", category: "", location: "", connectionType: "", room_number: "", internet_speed: "", issue_duration: "", mobile_number: "" });
  const [editImage, setEditImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [showDeleteSuccess, setShowDeleteSuccess] = useState(false);
  const [showSaveSuccess, setShowSaveSuccess] = useState(false);
  const [editWarning, setEditWarning] = useState("");
  const [showImageModal, setShowImageModal] = useState(false);
  const [modalImageSrc, setModalImageSrc] = useState(null);
  // Track expanded replies per comment (UserDashboard)
  const [expandedReplies, setExpandedReplies] = useState({});

  // Reply UI state for threaded replies
  const [openReply, setOpenReply] = useState(null);
  const [replyTexts, setReplyTexts] = useState({});

  const [showReopenModal, setShowReopenModal] = useState(false);
  const [selectedComplaintForReopen, setSelectedComplaintForReopen] = useState(null);
  const warningRef = useRef(null);
  const baseUrl = process.env.REACT_APP_COMPLAINTS_APP_BE_URL;

  
  // ---- UI-only filter bar state (display filtering only; does not touch API calls or business logic) ----
  const [filterCategory, setFilterCategory] = useState("All");
  const [filterStatus, setFilterStatus] = useState("All");
  const [searchTerm, setSearchTerm] = useState("");

  const firstName = user?.name || "User";

  useEffect(() => {
    if (editWarning && warningRef.current) {
      warningRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [editWarning]);

  useEffect(() => {
    if (!user?.email) return;

    const token = localStorage.getItem("authToken");

    axios
      .get(`${baseUrl}/user-api/view-complaints/${user.email}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        const fetchedComplaints = res.data.complaints || [];
        fetchedComplaints.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
        setComplaints(fetchedComplaints);

        const votes = {};
        (res.data.complaints || []).forEach((complaint) => {
          const vote = complaint.userVote || (
            Array.isArray(complaint.votedUsers)
              ? complaint.votedUsers.find((v) => v.email === user.email)?.vote
              : null
          );
          if (vote) votes[complaint.complaint_id] = vote;
        });

        setUserVotes(votes);
      })
      .catch((err) => {
        console.error(err);

        // Handle token expiry
        if (err.response && err.response.status === 401) {
          localStorage.removeItem("authToken"); // Remove invalid token
          navigate("/complaints-website");      // Redirect to login
        }
      });
  }, [user]);

  const toggleReplies = (commentId) => {
    setExpandedReplies((prev) => ({
      ...prev,
      [commentId]: !prev[commentId],
    }));
  };

  const formatDateTime = (timestamp) => {
    if (!timestamp) return "";

    const date = new Date(timestamp);
    const now = new Date();

    const timePart = date.toLocaleTimeString([], {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });

    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const startOfYesterday = new Date(startOfToday);
    startOfYesterday.setDate(startOfYesterday.getDate() - 1);

    if (date >= startOfToday) return `Today, ${timePart}`;
    if (date >= startOfYesterday) return `Yesterday, ${timePart}`;

    const diffDays = Math.floor((startOfToday - date) / (1000 * 60 * 60 * 24));
    return `${diffDays} days ago`;
  };

  const getVisibleAdminComments = (complaint) => {
    if (!complaint.comments) return [];

    return complaint.comments.filter((comment) =>
      comment.text &&
      !comment.text.includes("[Assistant Update]") &&
      !comment.text.toLowerCase().includes("updated status to")
    );
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return "";
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const minutes = Math.floor(diffMs / (1000 * 60));
    const hours = Math.floor(diffMs / (1000 * 60 * 60));
    const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const weeks = Math.floor(days / 7);
    const months = Math.floor(days / 30);
    const years = Math.floor(days / 365);

    if (minutes < 1) return "Just now";
    if (minutes < 60) return `${minutes} ${minutes === 1 ? "minute" : "minutes"} ago`;
    if (hours < 24) return `${hours} ${hours === 1 ? "hour" : "hours"} ago`;
    if (days === 1) return "Yesterday";
    if (days < 7) return `${days} days ago`;
    if (days < 30) return `${weeks} ${weeks === 1 ? "week" : "weeks"} ago`;
    if (days < 365) return `${months} ${months === 1 ? "month" : "months"} ago`;
    return `${years} ${years === 1 ? "year" : "years"} ago`;
  };

  // Reply helpers for threaded replies
  const handleReplyChange = (commentId, value) => {
    setReplyTexts((prev) => ({ ...prev, [commentId]: value }));
  };

  const handleReplySubmit = async (comment) => {
    const text = (replyTexts[comment.id] || "").trim();

    if (text.trim().length < 5) {
      return toast.error("Reply must be at least 5 characters or Please enter a meaningful reply");
    }

    try {
      const token = localStorage.getItem("authToken");
      const url = `${baseUrl}/user-api/complaints/${expandedCard.complaint_id}/comment/${comment.id}/reply`;
      const res = await axios.post(url, { text }, { headers: { Authorization: `Bearer ${token}` } });

      if (res?.data?.complaint) {
        setExpandedCard(res.data.complaint);
        setReplyTexts((prev) => ({ ...prev, [comment.id]: "" }));
        // Auto-expand the replies section to show the new reply immediately
        setExpandedReplies((prev) => ({ ...prev, [comment.id]: true }));
        setOpenReply(null);
        toast.success("Reply added successfully");
      } else {
        toast.info("Reply added, but failed to refresh comments");
      }
    } catch (err) {
      console.error("Error adding reply:", err);
      toast.error(
        err.response?.data?.message || "Failed to add reply"
      );
    }
  };

  const isITCategory = (category) => {
    if (!category) return false;
    const c = String(category).toLowerCase().replace(/&/g, "and").trim();
    if (c === "it") return true;
    if (c.includes("it") && c.includes("network")) return true;
    if (c.includes("it and networking") || c.includes("it networking") || c.includes("it/networking")) return true;
    return false;
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 1500 * 1024) {
        alert("File too large! Please upload ≤ 1.5MB.");
        return;
      }
      setEditImage(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const handleLike = async (id) => {
    if (!user?.email || likingComplaintId) return;

    const previousVote = userVotes[id] || null;
    const nextVote = previousVote === "upvote" ? null : "upvote";
    const previousComplaint = complaints.find(
      (complaint) => complaint.complaint_id === id
    );
    const previousExpandedCard = expandedCard;

    setLikingComplaintId(id);
    setUserVotes((previousVotes) => {
      const updatedVotes = { ...previousVotes };
      if (nextVote) updatedVotes[id] = nextVote;
      else delete updatedVotes[id];
      return updatedVotes;
    });
    setComplaints((previousComplaints) =>
      previousComplaints.map((complaint) => {
        if (complaint.complaint_id !== id) return complaint;
        const likes = Math.max(
          0,
          (Number(complaint.likes) || 0) + (nextVote ? 1 : -1)
        );
        const dislikes =
          previousVote === "downvote" && nextVote
            ? Math.max(0, (Number(complaint.dislikes) || 0) - 1)
            : Number(complaint.dislikes) || 0;
        return { ...complaint, likes, dislikes };
      })
    );

    if (previousExpandedCard?.complaint_id === id) {
      setExpandedCard((currentCard) => {
        if (!currentCard || currentCard.complaint_id !== id) return currentCard;
        const likes = Math.max(
          0,
          (Number(currentCard.likes) || 0) + (nextVote ? 1 : -1)
        );
        const dislikes =
          previousVote === "downvote" && nextVote
            ? Math.max(0, (Number(currentCard.dislikes) || 0) - 1)
            : Number(currentCard.dislikes) || 0;
        return { ...currentCard, likes, dislikes };
      });
    }

    try {
      const token = localStorage.getItem("authToken");
      await axios.post(
        `${baseUrl}/user-api/like-complaint/${id}`,
        { email: user.email },
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch (err) {
      setUserVotes((previousVotes) => {
        const updatedVotes = { ...previousVotes };
        if (previousVote) updatedVotes[id] = previousVote;
        else delete updatedVotes[id];
        return updatedVotes;
      });
      setComplaints((previousComplaints) =>
        previousComplaints.map((complaint) =>
          complaint.complaint_id === id && previousComplaint
            ? {
                ...complaint,
                likes: previousComplaint.likes,
                dislikes: previousComplaint.dislikes,
              }
            : complaint
        )
      );
      if (previousExpandedCard) {
        setExpandedCard(previousExpandedCard);
      }
      console.error("Error updating like:", err);
      toast.error(err.response?.data?.message || "Failed to update like");
    } finally {
      setLikingComplaintId((currentId) =>
        currentId === id ? null : currentId
      );
    }
  };

  const handleEditComplaint = (complaint) => {
    setEditComplaint(complaint);
    const it = complaint.it_details || {};
    setEditForm({
      title: complaint.title,
      description: complaint.description,
      category: complaint.category,
      // location: it.location || complaint.location || "",
      name: it.name || complaint.name || "",
      department: it.department || complaint.department || "",
      connectionType: it.connectionType || complaint.connectionType || "",
      room_number: it.room_number || complaint.room_number || "",
      internet_speed: it.internet_speed || complaint.internet_speed || "",
      issue_duration: it.issue_duration || complaint.issue_duration || "",
      mobile_number: it.mobile_number || complaint.mobile_number || "",
    });
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditForm((prev) => ({ ...prev, [name]: value }));
  };

  const removeEditImage = () => {
    setEditImage(null);
    setImagePreview(null);
    setEditComplaint((prev) => ({ ...prev, image: null }));
  };

  const submitEditComplaint = async () => {
    try {
      const title = editForm.title.trim();
      const description = editForm.description.trim();
      if (!title || !description) {
        setEditWarning("⚠ Title and description cannot be empty.");
        if (warningRef.current) {
          warningRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
        }
        return;
      }

      const token = localStorage.getItem("authToken");
      let imageUrl = editComplaint.image;

      if (editImage) {
        const imgData = new FormData();
        imgData.append("file", editImage);
        imgData.append("upload_preset", "complaint_uploads");
        const uploadRes = await axios.post(
          "https://api.cloudinary.com/v1_1/dbsrpikci/image/upload",
          imgData
        );
        imageUrl = uploadRes.data.secure_url;
      }

      let updatedData = { ...editForm, image: imageUrl };
      if (isITCategory(editForm.category)) {
        updatedData = {
          ...updatedData,
          it_details: {
            location: "Main Campus",
            name: editForm.name || "",
            department: editForm.department || "",
            connectionType: editForm.connectionType,
            room_number: editForm.room_number,
            internet_speed: editForm.internet_speed,
            issue_duration: editForm.issue_duration,
            mobile_number: editForm.mobile_number,
          },
        };
      }

      await axios.put(`${baseUrl}/user-api/edit-complaint/${editComplaint.complaint_id}`, updatedData, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setComplaints((prev) =>
        prev.map((c) => (c.complaint_id === editComplaint.complaint_id ? { ...c, ...updatedData } : c))
      );

      setShowSaveSuccess(true);
      setTimeout(() => setShowSaveSuccess(false), 3000);

      setEditComplaint(null);
      setEditImage(null);
      setImagePreview(null);
      setEditWarning("");
    } catch (err) {
      setEditWarning(err.response?.data?.message || "Error saving complaint");
    }
  };

  const handleDeleteComplaint = async (id) => {
    if (!window.confirm("Are you sure you want to delete this complaint?")) return;
    try {
      const token = localStorage.getItem("authToken");
      await axios.delete(`${baseUrl}/user-api/delete-complaint/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setComplaints((prev) => prev.filter((c) => c.complaint_id !== id));
      setShowDeleteSuccess(true);
      setTimeout(() => setShowDeleteSuccess(false), 3000);
      if (expandedCard?.complaint_id === id) setExpandedCard(null);
      if (editComplaint?.complaint_id === id) setEditComplaint(null);
    } catch (err) {
      console.error(err);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "Pending":
        return <span className="status-pill status-pending">Pending</span>;
      case "Ongoing":
        return <span className="status-pill status-ongoing">Ongoing</span>;
      case "Resolved":
        return <span className="status-pill status-resolved">Resolved</span>;
      case "Reopened":
        return <span className="status-pill status-reopened">🔄 Reopened</span>;
      default:
        return null;
    }
  };

  // ---- Display-only filtering. Purely additive UI behaviour; does not mutate
  // `complaints` state or touch any API / business logic. ----
  const filteredComplaints = useMemo(() => {
    return complaints.filter((c) => {
      const matchesCategory = filterCategory === "All" || c.category === filterCategory;
      const matchesStatus = filterStatus === "All" || c.status === filterStatus;
      const matchesSearch =
        searchTerm.trim() === "" ||
        (c.title && c.title.toLowerCase().includes(searchTerm.trim().toLowerCase())) ||
        (c.description && c.description.toLowerCase().includes(searchTerm.trim().toLowerCase()));
      return matchesCategory && matchesStatus && matchesSearch;
    });
  }, [complaints, filterCategory, filterStatus, searchTerm]);

  const hasComplaints = complaints.length > 0;
  const hasFilteredResults = filteredComplaints.length > 0;

  return (
    <div className="user-dashboard-container">
      <div className="dashboard-container">
        <ToastContainer />

        {/* ---------------- Simple greeting (replaces large welcome card) ---------------- */}
        <div className="greeting-section mb-4">
          <h1 className="greeting-title">Hi, {firstName} 👋</h1>
          <p className="greeting-subtitle">Welcome back! Track and manage your complaints.</p>
        </div>

        {/* ---------------- Modern filter bar ---------------- */}
        <div className="modern-filter-container mb-5">
          <div className="modern-dropdown-row">
            <div className="modern-dropdown">
              <span className="dropdown-title">📂 Category</span>
              <select value={filterCategory} onChange={(e) => setFilterCategory(e.target.value)}>
                <option value="All">All Categories</option>
                {CATEGORIES.map((cat) => (
                  <option key={cat} value={cat}>
                    {cat}
                  </option>
                ))}
              </select>
            </div>

            <div className="modern-dropdown">
              <span className="dropdown-title">📌 Status</span>
              <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
                <option value="All">All Status</option>
                {STATUSES.map((status) => (
                  <option key={status} value={status}>
                    {status}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* ---------------- Section heading (only when complaints exist) ---------------- */}
        {hasComplaints && (
          <div className="my-complaints-heading mb-3">
            <h3>My Complaints</h3>
          </div>
        )}

        {/* ==================== COMPLAINT CARDS (Home-style grid) ==================== */}
        <Container className="mt-2 dashboard-cards-container mb-2">
          {!hasComplaints ? (
            /* ---------------- Modern empty state (no complaints at all) ---------------- */
            <div className="empty-state-modern">
              <div className="empty-state-icon">
                <Inbox size={40} strokeWidth={1.5} />
              </div>
              <h4 className="empty-state-title">No Complaints yet</h4>
              <p className="empty-state-sub">
                Have something that needs attention? Raise a complaint and help us
                improve your campus experience.
              </p>
              <button className="raise-btn-modern" onClick={() => navigate("/complaint-form")}>
                <FaPlus size={14} /> Raise Complaint
              </button>
            </div>
          ) : !hasFilteredResults ? (
            /* ---------------- No results match the current filters ---------------- */
            <div className="empty-state-modern">
              <div className="empty-state-icon">
                <Search size={40} strokeWidth={1.5} />
              </div>
              <h4 className="empty-state-title">No matching requests</h4>
              <p className="empty-state-sub">
                Try adjusting your category, status, or search to find what you're looking for.
              </p>
            </div>
          ) : (
            <Row
              className="complaints-grid-row"
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(3, 1fr)",
                gap: "1.75rem",
                alignItems: "stretch",
                margin: 0,
                width: "100%",
              }}
            >
              {filteredComplaints.map((complaint, index) => {
                const isItCard = isITCategory(complaint.category);
                const description = complaint.description || "";
                const shouldTruncate = isItCard
                  ? description.length > 90
                  : description.length > 200;
                const isExpanded = expandedDescriptions[complaint.complaint_id] === true;
                const previewDescription = description.substring(
                  0,
                  isItCard ? 90 : 200
                );
                return (
                  <Col
                    key={complaint.complaint_id}
                    className="complaints-grid-col"
                    style={{
                      width: "100%",
                      maxWidth: "none",
                      flex: "none",
                      padding: 0,
                      margin: 0,
                      animation: `fadeInUp 0.45s ease-out ${0.05 * index}s both`,
                    }}
                  >
                    <Card
                      className={`card-hover-effect glass-effect rounded-4 custom-card-container w-100 d-flex flex-column ${isItCard ? "it-card-roomy" : ""}`}
                      style={{
                        height: "100%",
                        minHeight: "420px",
                        maxHeight: "600px",
                        display: "flex",
                        flexDirection: "column",
                        overflow: "hidden",
                      }}
                    >
                      {/* Complaint Image */}
                      <div className="position-relative complaint-image-wrapper">
                        {complaint.image ? (
                          <img
                            src={complaint.image}
                            alt="Complaint"
                            className="complaint-image"
                            onClick={() => { setModalImageSrc(complaint.image); setShowImageModal(true); }}
                          />
                        ) : (
                          <div className="no-image-placeholder">
                            <ImageOff size={30} strokeWidth={1.6} />
                            <span>No Image Available</span>
                          </div>
                        )}

                        <div
                          className="status-overlay position-absolute"
                          style={{ top: "10px", left: "10px", zIndex: 3 }}
                        >
                          {!complaint.flagged?.isFlagged && getStatusBadge(complaint.status)}
                        </div>
                      </div>

                      <div className="card-body-content">
                        <div className="d-flex align-items-center justify-content-between">
                          <div className="complaint-date-row mb-0">
                            <span className="date-icon-wrap">
                              <FaCalendarAlt />
                            </span>
                            {formatDate(complaint.timestamp)}

                            {complaint.flagged?.isFlagged && (
                              <span
                                className="flagged-pill-row"
                                onClick={() => setExpandedCard(complaint)}
                              >
                                🚩 Flagged
                              </span>
                            )}
                          </div>

                          {complaint.user_id === user?.email && complaint.status === "Pending" && (
                            <FaEdit
                              size={20}
                              className="edit-icon-purple"
                              onClick={() => handleEditComplaint(complaint)}
                            />
                          )}
                        </div>

                        <Card.Title className="card-title-text">
                          {complaint.title}
                        </Card.Title>

                        <div style={{ flex: 1 }}>
                          <Card.Text
                            className={`card-desc-text mb-2 ${isItCard ? "desc-clamp-2" : ""} ${isExpanded ? "expanded" : ""}`}
                          >
                            {isExpanded
                              ? description
                              : `${previewDescription}${shouldTruncate ? "..." : ""}`}
                          </Card.Text>
                          {shouldTruncate && (
                            <button
                              type="button"
                              className="expand-desc-btn"
                              onClick={() =>
                                setExpandedDescriptions((previous) => ({
                                  ...previous,
                                  [complaint.complaint_id]: !isExpanded,
                                }))
                              }
                              aria-expanded={isExpanded}
                              aria-label={isExpanded ? "Show less description" : "Show more description"}
                            >
                              {isExpanded ? (
                                <><ChevronUp size={16} /> Show Less</>
                              ) : (
                                <><ChevronDown size={16} /> Show More</>
                              )}
                            </button>
                          )}
                        </div>

                        {(() => {
                          const isIt = isITCategory(complaint.category);
                          const it = complaint.it_details || {};
                          // const itLocation = it.location;
                          const itDepartment = it.department || complaint.department;
                          const itName = it.name || complaint.name;
                          const itConnection = it.connectionType;
                          const itRoom = it.room_number || complaint.room_number;
                          const itSpeed = it.internet_speed || complaint.internet_speed;
                          const itDuration = it.issue_duration || complaint.issue_duration;
                          const itMobile = it.mobile_number || complaint.mobile_number;
                          if (isIt && ( itConnection || itRoom || itSpeed || itDuration || itMobile)) {
                            return (
                              <div className="it-summary mb-2">
                                {/* {(itLocation || itConnection) && (
                                  <div className="d-flex align-items-center mb-1" style={{ gap: 12 }}>
                                    {itLocation && (
                                      <div className="d-flex align-items-center">
                                        <span style={{ marginRight: 6 }}>📍</span>
                                        <small>{itLocation}</small>
                                      </div>
                                    )}
                                    {itConnection && (
                                      <div className="d-flex align-items-center">
                                        <span style={{ marginRight: 6 }}>🔗</span>
                                        <small>{itConnection}</small>
                                      </div>
                                    )}
                                  </div>
                                )} */}
                                {itDepartment && (
                                  <div className="d-flex align-items-center mb-1">
                                    <span style={{ marginRight: 8 }}>🏢</span>
                                    <small><strong>Department:</strong>&nbsp;{itDepartment}</small>
                                  </div>
                                )}
                                {itName && (
                                  <div className="d-flex align-items-center mb-1">
                                    <span style={{ marginRight: 8 }}>👤</span>
                                    <small><strong>Name:</strong>&nbsp;{itName}</small>
                                  </div>
                                )}
                                {itConnection && (
                                <div className="d-flex align-items-center mb-1">
                                <span style={{ marginRight: 8 }}>🔗</span>
                                <small><strong>Connection:</strong>&nbsp;{itConnection}</small>
                                </div>
          )}
                                {itRoom && (
                                  <div className="d-flex align-items-center mb-1">
                                    <span style={{ marginRight: 8 }}>🏷️</span>
                                    <small><strong>Room:</strong>&nbsp;{itRoom}</small>
                                  </div>
                                )}
                                {itSpeed && (
                                  <div className="d-flex align-items-center mb-1">
                                    <span style={{ marginRight: 8 }}>📶</span>
                                    <small><strong>Internet Speed:</strong>&nbsp;{itSpeed}</small>
                                  </div>
                                )}
                                {itDuration && (
                                  <div className="d-flex align-items-center mb-1">
                                    <span style={{ marginRight: 8 }}>⏱️</span>
                                    <small><strong>Duration:</strong>&nbsp;{itDuration}</small>
                                  </div>
                                )}
                                {itMobile && (
                                  <div className="d-flex align-items-center mb-1">
                                    <span style={{ marginRight: 8 }}>📞</span>
                                    <small><strong>Mobile:</strong>&nbsp;{itMobile}</small>
                                  </div>
                                )}
                              </div>
                            );
                          }
                          return null;
                        })()}

                        {getVisibleAdminComments(complaint).length > 0 && (
                          <button className="admin-update-btn" onClick={() => setExpandedCard(complaint)}>
                            Show Admin Updates <ChevronDown size={16} />
                          </button>
                        )}

                        {complaint.status === "Resolved" && (
                          <button
                            className="issue-not-fixed-attractive-btn"
                            onClick={() => {
                              setSelectedComplaintForReopen(complaint);
                              setShowReopenModal(true);
                            }}
                          >
                            <i className="bi bi-exclamation-circle issue-icon"></i>
                            Issue Not Fixed?
                          </button>
                        )}

                        <div className="mt-auto d-flex w-100 align-items-center pt-2 px-0 card-footer-row">
                          <span className="category-tag">
                            {complaint.category}
                          </span>
                          <div className="d-flex align-items-center gap-2 ms-auto">
                            <button
                              type="button"
                              className={`vote-btn vote-btn-up ${userVotes[complaint.complaint_id] === "upvote" ? "active" : ""}`}
                              onClick={() => handleLike(complaint.complaint_id)}
                              disabled={likingComplaintId === complaint.complaint_id}
                              aria-pressed={userVotes[complaint.complaint_id] === "upvote"}
                              aria-label={userVotes[complaint.complaint_id] === "upvote" ? "Remove like" : "Like complaint"}
                            >
                              <ThumbsUp size={18} /> {complaint.likes}
                            </button>
                          </div>
                        </div>
                      </div>
                    </Card>
                  </Col>
                );
              })}
            </Row>
          )}

          {hasComplaints && (
            <button className="add-complaint-btn" onClick={() => navigate("/complaint-form")}>
              <FaPlus className="plus-icon" /> Register Complaint
            </button>
          )}
        </Container>

        {/* Expanded Card Modal (unchanged) */}
        {expandedCard && (
          <div className="overlay">
            <Card className="popup-card rounded-4 card-background-gradient p-4">
              <button className="close-btn-inside-modalh" onClick={() => setExpandedCard(null)}>✕</button>

              {expandedCard.flagged?.isFlagged ? (
                <div className="flagged-container">
                  <div className="flagged-title-row">
                    <span className="flag-emoji">🚩</span>
                    <h4 className="flagged-title-text">This request has been flagged</h4>
                  </div>

                  <p className="flagged-line">
                    <strong>Reason:</strong> {expandedCard.flagged.reason}
                  </p>

                  {expandedCard.flagged.note && (
                    <>
                      <p className="flagged-line"><strong>Note:</strong></p>
                      <div className="flag-note-box">{expandedCard.flagged.note}</div>
                    </>
                  )}

                  <p className="flagged-line">
                    <strong>Flagged By:</strong> {expandedCard.flagged.flaggedBy}
                  </p>
                </div>
              ) : (
                <div className="mb-2">{getStatusBadge(expandedCard.status)}</div>
              )}

              {/* Date */}
              <Card.Text className="mt-1 mb-2" style={{ fontSize: "0.9rem", fontWeight: 600, color: "#1e90ff" }}>
                <span
                  style={{
                    backgroundColor: "#d4dbe2ff",
                    color: "#1e90ff",
                    padding: "6px",
                    borderRadius: "10px",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: "28px",
                    height: "28px",
                    marginRight: "8px",
                  }}
                >
                  <FaCalendarAlt style={{ fontSize: "14px" }} />
                </span>
                {formatDate(expandedCard.timestamp)}
              </Card.Text>

              {/* Title */}
              <Card.Title className="fw-bold text-dark mt-3 fs-4">{expandedCard.title}</Card.Title>

              {/* Description */}
              <Card.Text className="text-dark mb-3">{expandedCard.description}</Card.Text>

              {(() => {
                const isIt = isITCategory(expandedCard.category);
                const it = expandedCard.it_details || {};
                // const itLocation = it.location;
                const itDepartment = it.department || expandedCard.department;
                const itName = it.name || expandedCard.name;
                const itConnection = it.connectionType;
                const itRoom = it.room_number || expandedCard.room_number;
                const itSpeed = it.internet_speed || expandedCard.internet_speed;
                const itDuration = it.issue_duration || expandedCard.issue_duration;
                const itMobile = it.mobile_number || expandedCard.mobile_number;
                if (isIt && ( itConnection || itRoom || itSpeed || itDuration || itMobile)) {
                  return (
                    <div className="it-summary-popup mb-3" style={{ color: "#495057" }}>
                      {/* {itLocation && (
                        <div className="d-flex align-items-center mb-1">
                          <span style={{ marginRight: 10 }}>📍</span>
                          <strong>Location:</strong>&nbsp; <span>{itLocation}</span>
                        </div>
                      )}
                      {itConnection && (
                        <div className="d-flex align-items-center mb-1">
                          <span style={{ marginRight: 10 }}>🔗</span>
                          <strong>Connection Type:</strong>&nbsp; <span>{itConnection}</span>
                        </div>
                      )} */}
                      {itDepartment && (
                        <div className="d-flex align-items-center mb-1">
                          <span style={{ marginRight: 8 }}>🏢</span>
                          <small><strong>Department:</strong>&nbsp;{itDepartment}</small>
                        </div>
                      )}
                      {itName && (
                        <div className="d-flex align-items-center mb-1">
                          <span style={{ marginRight: 8 }}>👤</span>
                          <small><strong>Name:</strong>&nbsp;{itName}</small>
                        </div>
                      )}
                      {itConnection && (
                     <div className="d-flex align-items-center mb-1">
                     <span style={{ marginRight: 8 }}>🔗</span>
                      <small><strong>Connection:</strong>&nbsp;{itConnection}</small>
                      </div>
        )}
                      {itRoom && (
                        <div className="d-flex align-items-center mb-1">
                          <span style={{ marginRight: 10 }}>🏷️</span>
                          <strong>Room:</strong>&nbsp; <span>{itRoom}</span>
                        </div>
                      )}
                      {itSpeed && (
                        <div className="d-flex align-items-center mb-1">
                          <span style={{ marginRight: 10 }}>📶</span>
                          <strong>Internet Speed:</strong>&nbsp; <span>{itSpeed}</span>
                        </div>
                      )}
                      {itDuration && (
                        <div className="d-flex align-items-center mb-1">
                          <span style={{ marginRight: 10 }}>⏱️</span>
                          <strong>Duration:</strong>&nbsp; <span>{itDuration}</span>
                        </div>
                      )}
                      {itMobile && (
                        <div className="d-flex align-items-center mb-1">
                          <span style={{ marginRight: 10 }}>📞</span>
                          <strong>Mobile:</strong>&nbsp; <span>{itMobile}</span>
                        </div>
                      )}
                    </div>
                  );
                }
                return null;
              })()}

              {/* Admin Updates */}
              <div className="admin-updates mb-3">
                <h5 className="mb-2">
                  <MdOutlineTextsms className="me-2" /> Admin Updates:
                </h5>
                {getVisibleAdminComments(expandedCard).length > 0 ? (
                  getVisibleAdminComments(expandedCard).map((comment) => {
                    const isStudent = comment.role === "student";
                    const displayName = isStudent ? "Student" : (comment.email || "Admin");
                    const borderColor = isStudent ? "#ff6b6b" : "purple";
                    return (
                      <div
                        key={comment.id}
                        className="update-entry mb-2 p-3"
                        style={{ backgroundColor: "#f8f9fa", borderLeft: `4px solid ${borderColor}`, borderRadius: "10px" }}
                      >
                        <div className="d-flex align-items-center mb-1">
                          <FaUser className="me-2" size={18} style={{ color: borderColor }} />
                          <strong style={{ color: borderColor }}>{displayName}</strong>
                        </div>
                        <div style={{ marginLeft: "1.8rem" }}>{comment.text}</div>

                        {/* Replies */}
                        {comment.replies && comment.replies.length > 0 && (
                          <div style={{ marginLeft: "2.4rem", marginTop: "0.6rem" }}>
                            {(expandedReplies[comment.id]
                              ? comment.replies
                              : comment.replies.slice(0, 2)
                            ).map((r) => (
                              <div key={r.id} style={{ marginBottom: "0.5rem" }}>
                                <div
                                  className="reply-card"
                                  style={{
                                    backgroundColor: "#fff",
                                    borderLeft: "3px solid #e9ecef",
                                    padding: "8px",
                                    borderRadius: "6px",
                                  }}
                                >
                                  <div style={{ display: "flex", alignItems: "center", marginBottom: "4px" }}>
                                    {r.role === "student" && (
                                      <FaUser size={14} className="me-2" style={{ color: "#ff6b6b" }} />
                                    )}

                                    <strong
                                      style={{
                                        color: r.role === "student" ? "#ff6b6b" : "#6c757d",
                                        fontSize: "0.9rem",
                                      }}
                                    >
                                      {r.role === "student" ? "Student" : r.email || "User"}
                                    </strong>

                                    <span
                                      style={{
                                        marginLeft: "auto",
                                        fontSize: "0.75rem",
                                        color: "#6c757d",
                                      }}
                                    >
                                      {formatDateTime(r.timestamp || r.date)}
                                    </span>
                                  </div>

                                  <div style={{ marginLeft: "0.8rem" }}>{r.text}</div>
                                </div>
                              </div>
                            ))}

                            {/* View More / View Less Replies */}
                            {comment.replies.length > 2 && (
                              <div style={{ marginLeft: "2.5rem" }}>
                                <button
                                  className="btn btn-link p-0"
                                  style={{
                                    fontSize: "1rem",
                                    fontWeight: 500,
                                  }}
                                  onClick={() => toggleReplies(comment.id)}
                                >
                                  {expandedReplies[comment.id]
                                    ? "View less replies"
                                    : `View more replies (${comment.replies.length - 2})`}
                                </button>
                              </div>
                            )}
                          </div>
                        )}

                        {/* Reply UI for complaint owner when target comment is admin */}
                        {comment.role === "admin" && userEmail === expandedCard.user_id && (
                          <div style={{ marginLeft: "1.8rem", marginTop: "0.5rem" }}>
                            {openReply === comment.id ? (
                              <div className="d-flex gap-2">
                                <textarea
                                  className="form-control"
                                  rows="2"
                                  value={replyTexts[comment.id] || ""}
                                  onChange={(e) => handleReplyChange(comment.id, e.target.value)}
                                  placeholder="Write a reply..."
                                ></textarea>
                                <div className="d-flex flex-column gap-1 ms-2">
                                  <button className="btn btn-primary btn-sm" onClick={() => handleReplySubmit(comment)}>Send</button>
                                  <button className="btn btn-secondary btn-sm" onClick={() => { setOpenReply(null); setReplyTexts((prev) => ({ ...prev, [comment.id]: "" })); }}>Cancel</button>
                                </div>
                              </div>
                            ) : (
                              <button className="btn btn-link btn-sm" onClick={() => setOpenReply(comment.id)}>Reply</button>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })
                ) : (
                  <p>No admin updates available.</p>
                )}
              </div>
            </Card>
          </div>
        )}

        {/* Edit Complaint Modal (unchanged) */}
        {editComplaint && (
          <div className="overlay">
            <Card
              style={{ position: "relative" }}
              className=" popup-card rounded-4 card-background-gradient p-3"
              onClick={(e) => e.stopPropagation()}
            >
              <button className="close-btn-inside-modalm fixed" onClick={() => setEditComplaint(null)}>✕</button>

              <div className="d-flex justify-content-between align-items-center mb-2">
                <h4 className="editheading mb-2 text-bold text-dark">Edit or delete your Complaint</h4>
                <button
                  className="btn btn-danger btn-icon btn-sm delete-btn"
                  onClick={() => handleDeleteComplaint(editComplaint.complaint_id)}
                >
                  <i className="bi bi-trash"></i>
                </button>
              </div>

              <div className="form-group mb-2">
                <label className="form-label d-flex align-items-center">
                  <span className="label-icon purple me-2">
                    <i className="bi bi-info-lg"></i>
                  </span>
                  Title
                </label>
                <input
                  type="text"
                  name="title"
                  className="form-control"
                  value={editForm.title}
                  onChange={handleEditChange}
                />
              </div>

              <div className="form-group mb-2">
                <label className="form-label d-flex align-items-center">
                  <span className="label-icon dark-purple me-2">
                    <i className="bi bi-card-text"></i>
                  </span>
                  Description
                </label>
                <textarea
                  name="description"
                  className="form-control"
                  rows={4}
                  value={editForm.description}
                  onChange={handleEditChange}
                />
              </div>

              <div className="form-group mb-2">
                <label className="form-label d-flex align-items-center">
                  <span className="label-icon orange me-2">
                    <i className="bi bi-tag"></i>
                  </span>
                  Category
                </label>
                <select
                  name="category"
                  className="form-control"
                  value={editForm.category}
                  onChange={handleEditChange}
                  disabled
                >
                  {CATEGORIES.map((cat) => (
                    <option key={cat} value={cat}>
                      {cat}
                    </option>
                  ))}
                </select>
              </div>

              {isITCategory(editForm.category) && (
                <div className="it-edit-fields mb-2">
                  {/* <div className="form-group mb-2">
                    <label className="form-label">Location</label>
                    <select
                      name="location"
                      className="form-control"
                      value={editForm.location}
                      onChange={handleEditChange}
                    >
                      <option value="">-- Select Location --</option>
                      <option value="Main Campus">Main Campus</option>
                     
                    </select>
                  </div> */}

                 
                    <div className="form-group mb-2">
                      <label className="form-label">Connection Type</label>
                      <select
                        name="connectionType"
                        className="form-control"
                        value={editForm.connectionType}
                        onChange={handleEditChange}
                      >
                        <option value="">-- Select Connection --</option>
                        <option value="WiFi">WiFi</option>
                        <option value="LAN">LAN</option>
                      </select>
                    </div>
                 

                  {/* {editForm.location && editForm.connectionType === "WiFi" && (editForm.location === "Boys Hostel" || editForm.location === "Girls Hostel") ? (
                    <div style={{ padding: "12px", backgroundColor: "#fff3cd", border: "1px solid #ffc107", borderRadius: "4px", marginBottom: "15px", color: "#856404", marginTop: "10px" }}>
                      <strong>⚠️ Warning:</strong> WiFi is not supported at hostels. Please use LAN or contact IT support for assistance.
                    </div>
                  ) : null} */}

                 
                    <>
                      <div className="form-group mb-2">
                        <label className="form-label">Room Number</label>
                        <input
                          type="text"
                          name="room_number"
                          className="form-control"
                          value={editForm.room_number}
                          onChange={handleEditChange}
                        />
                      </div>

                      <div className="form-group mb-2">
                        <label className="form-label">Internet Speed</label>
                        <input
                          type="text"
                          name="internet_speed"
                          className="form-control"
                          value={editForm.internet_speed}
                          onChange={handleEditChange}
                        />
                      </div>

                      <div className="form-group mb-2">
                        <label className="form-label">Issue Duration</label>
                        <input
                          type="text"
                          name="issue_duration"
                          className="form-control"
                          value={editForm.issue_duration}
                          onChange={handleEditChange}
                        />
                      </div>

                      <div className="form-group mb-2">
                        <label className="form-label">Mobile Number</label>
                        <input
                          type="tel"
                          name="mobile_number"
                          className="form-control"
                          value={editForm.mobile_number}
                          onChange={handleEditChange}
                        />
                      </div>
                    </>
                
                </div>
              )}

              <div className="form-group mb-2">
                <label className="form-label d-flex align-items-center">
                  <span className="label-icon blue me-2">
                    <i className="bi bi-image"></i>
                  </span>
                  Update Image (Optional)
                </label>

                <div className="upload-box-wrapper">
                  <div className="upload-box">

                    {imagePreview || editComplaint.image ? (
                      <div className="image-preview">
                        <img
                          src={imagePreview || editComplaint.image}
                          alt="Preview"
                          onClick={() => {
                            setModalImageSrc(imagePreview || editComplaint.image);
                            setShowImageModal(true);
                          }}
                          style={{
                            width: "100%",
                            borderRadius: "8px",
                            maxHeight: "250px",
                            objectFit: "cover",
                            cursor: "pointer",
                          }}
                        />
                        <button type="button" className="remove-btn" onClick={removeEditImage}>
                          ×
                        </button>
                      </div>
                    ) : (
                      <>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleImageChange}
                          id="edit-file-upload"
                          style={{ display: "none" }}
                        />
                        <label htmlFor="edit-file-upload" className="upload-placeholder">
                          <div className="upload-icon">⬆</div>
                          <span className="upload-text">Upload Image</span>
                        </label>
                      </>
                    )}

                  </div>

                  <p className="file-size-note text-dark">≤ 1.5MB</p>
                </div>
              </div>

              {editWarning && (
                <div
                  ref={warningRef}
                  className="alert alert-warning text-center mb-3"
                  style={{ borderRadius: "10px", fontWeight: 500 }}
                >
                  ⚠ {editWarning}
                </div>
              )}

              <div className="modal-footer mt-2 text-center">
                <button className="btn btn-primary save-btn" onClick={submitEditComplaint}>
                  <i className="bi bi-save me-2"></i> Save Changes
                </button>
              </div>
            </Card>
          </div>
        )}

        <Modal
          show={showImageModal}
          onHide={() => setShowImageModal(false)}
          centered
          size="lg"
          contentClassName="bg-dark border-0"
          style={{ zIndex: 9999 }}
          backdropClassName="custom-image-backdrop"
        >
          <Modal.Body
            className="p-0 d-flex justify-content-center align-items-center"
            style={{ backgroundColor: "rgba(0,0,0,0.9)" }}
          >
            <img
              src={modalImageSrc}
              alt="Full preview"
              style={{
                width: "auto",
                maxWidth: "80%",
                height: "auto",
                maxHeight: "65vh",
                borderRadius: "10px",
                objectFit: "contain",
                boxShadow: "0 0 15px rgba(0,0,0,0.5)",
              }}
            />
            <button
              onClick={() => setShowImageModal(false)}
              style={{
                position: "absolute",
                top: "15px",
                right: "20px",
                background: "rgba(255,255,255,0.2)",
                color: "white",
                border: "none",
                borderRadius: "50%",
                width: "35px",
                height: "35px",
                fontSize: "1.5rem",
                cursor: "pointer",
                zIndex: 10000,
              }}
            >
              ×
            </button>
          </Modal.Body>
        </Modal>

        <Modal
          show={showDeleteSuccess}
          onHide={() => setShowDeleteSuccess(false)}
          centered
        >
          <Modal.Body className="text-center p-5">
            <CheckCircleFill size={50} color="green" className="mb-3" />
            <h5 className="text-success">Complaint Deleted Successfully!</h5>
          </Modal.Body>
        </Modal>

        <Modal
          show={showSaveSuccess}
          onHide={() => setShowSaveSuccess(false)}
          centered
        >
          <Modal.Body className="text-center p-5">
            <CheckCircleFill size={50} color="green" className="mb-3" />
            <h5 className="text-success">Complaint Saved Successfully!</h5>
          </Modal.Body>
        </Modal>

        {/* Reopen Complaint Modal */}
        <ReopenComplaintModal
          show={showReopenModal}
          onHide={() => setShowReopenModal(false)}
          complaintId={selectedComplaintForReopen?.complaint_id}
          userEmail={user?.email}
          baseUrl={baseUrl}
          onSuccess={() => {
            toast.success("Complaint reopened! Admins have been notified.", { position: "top-right", autoClose: 4000 });

            const token = localStorage.getItem("authToken");
            axios
              .get(`${baseUrl}/user-api/view-complaints/${user.email}`, {
                headers: { Authorization: `Bearer ${token}` },
              })
              .then((res) => {
                setComplaints(res.data.complaints || []);
              })
              .catch((err) => console.error(err));
          }}
        />

      </div>
    </div>
  );
};

export default UserDashboard;